import React, { useState, useEffect, useCallback } from 'react';
import {
  FiTag, FiRefreshCw, FiPlus, FiEdit2, FiTrash2, FiCheck, FiX,
  FiAlertCircle, FiCopy, FiFilter, FiDollarSign, FiPercent, FiCheckCircle,
} from 'react-icons/fi';
import {
  getAdminPromoCodes, createAdminPromoCode, updateAdminPromoCode,
  deleteAdminPromoCode, PromoCode,
} from '../../api/admin';

/* ── helpers ──────────────────────────────────────────────────────────────────── */
const PLAN_OPTIONS = ['free', 'standard', 'entreprise', 'business'];

const getCodeStatus = (c: PromoCode): 'active' | 'expired' | 'exhausted' | 'inactive' => {
  if (!c.is_active) return 'inactive';
  if (c.valid_until && new Date(c.valid_until) < new Date()) return 'expired';
  if (c.max_uses !== null && c.used_count >= c.max_uses) return 'exhausted';
  return 'active';
};

const STATUS_STYLE = {
  active:    'bg-green-100 text-green-700',
  expired:   'bg-red-100 text-red-600',
  exhausted: 'bg-orange-100 text-orange-600',
  inactive:  'bg-gray-100 text-gray-500',
};
const STATUS_LABEL = {
  active: 'Actif', expired: 'Expiré', exhausted: 'Épuisé', inactive: 'Inactif',
};

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '∞';

const emptyForm = {
  code: '', description: '',
  discount_type: 'percentage' as 'percentage' | 'fixed_amount',
  discount_value: '', max_uses: '', max_uses_per_customer: '1',
  valid_from: '', valid_until: '',
  min_purchase_amount: '',
  applicable_plans: [] as string[],
  is_active: true,
};

/* ── composant principal ─────────────────────────────────────────────────────── */
const AdminPromoCodes: React.FC = () => {
  const [codes, setCodes]         = useState<PromoCode[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [statusFilter, setFilter] = useState<string>('');
  const [showForm, setShowForm]   = useState(false);
  const [editId, setEditId]       = useState<string | null>(null);
  const [form, setForm]           = useState({ ...emptyForm });
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteId, setDeleteId]   = useState<string | null>(null);
  const [copied, setCopied]       = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await getAdminPromoCodes();
      setCodes(data.promo_codes);
    } catch { setError('Impossible de charger les codes promo'); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── Stats ── */
  const stats = {
    total:    codes.length,
    active:   codes.filter(c => getCodeStatus(c) === 'active').length,
    uses:     codes.reduce((s, c) => s + (c.used_count || 0), 0),
    discount: codes.reduce((s, c) => s + (Number(c.total_discount_given) || 0), 0),
  };

  /* ── Filtre ── */
  const visible = statusFilter
    ? codes.filter(c => getCodeStatus(c) === statusFilter)
    : codes;

  /* ── Clipboard ── */
  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  /* ── Formulaire ── */
  const openCreate = () => {
    setEditId(null); setForm({ ...emptyForm }); setFormError(null); setShowForm(true);
  };

  const openEdit = (c: PromoCode) => {
    setEditId(c.id);
    setForm({
      code: c.code,
      description: c.description || '',
      discount_type: c.discount_type,
      discount_value: String(c.discount_value),
      max_uses: c.max_uses !== null ? String(c.max_uses) : '',
      max_uses_per_customer: String(c.max_uses_per_customer || 1),
      valid_from: '',
      valid_until: c.valid_until ? c.valid_until.slice(0, 10) : '',
      min_purchase_amount: c.min_purchase_amount ? String(c.min_purchase_amount) : '',
      applicable_plans: c.applicable_plans || [],
      is_active: c.is_active,
    });
    setFormError(null); setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.code || !form.discount_value) {
      setFormError('Code et valeur de réduction sont obligatoires');
      return;
    }
    setSaving(true); setFormError(null);
    try {
      const payload = {
        code: form.code,
        description: form.description || null,
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        max_uses_per_customer: parseInt(form.max_uses_per_customer) || 1,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        min_purchase_amount: form.min_purchase_amount ? parseFloat(form.min_purchase_amount) : null,
        applicable_plans: form.applicable_plans.length > 0 ? form.applicable_plans : null,
        is_active: form.is_active,
      };
      if (editId) await updateAdminPromoCode(editId, payload);
      else        await createAdminPromoCode(payload);
      setShowForm(false); load();
    } catch (err: any) {
      setFormError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try { await deleteAdminPromoCode(id); setDeleteId(null); load(); }
    catch (err: any) { alert(err.response?.data?.error || 'Impossible de supprimer ce code'); }
  };

  const togglePlan = (p: string) =>
    setForm(f => ({
      ...f,
      applicable_plans: f.applicable_plans.includes(p)
        ? f.applicable_plans.filter(x => x !== p)
        : [...f.applicable_plans, p],
    }));

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Codes Promotionnels</h2>
          <p className="text-sm text-gray-500 mt-0.5">{stats.total} code{stats.total !== 1 ? 's' : ''} au total</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition">
            <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg text-sm font-medium hover:bg-pink-700 transition">
            <FiPlus size={14} /> Nouveau code
          </button>
        </div>
      </div>

      {/* ── Stats cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total codes', value: stats.total,                     icon: <FiTag size={16} />,        color: 'bg-pink-50 text-pink-600' },
          { label: 'Codes actifs', value: stats.active,                   icon: <FiCheckCircle size={16} />, color: 'bg-green-50 text-green-600' },
          { label: 'Utilisations', value: stats.uses,                     icon: <FiPercent size={16} />,    color: 'bg-blue-50 text-blue-600' },
          { label: 'Remises totales', value: `${stats.discount.toFixed(2)} €`, icon: <FiDollarSign size={16} />, color: 'bg-purple-50 text-purple-600' },
        ].map(({ label, value, icon, color }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>{icon}</div>
            <div>
              <p className="text-xs text-gray-500">{label}</p>
              <p className="text-lg font-bold text-gray-900">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <FiAlertCircle size={15} /> {error}
        </div>
      )}

      {/* ── Filtre statut ── */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <FiFilter size={14} className="text-gray-400" />
        {(['', 'active', 'expired', 'exhausted', 'inactive'] as const).map(s => (
          <button key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
              statusFilter === s
                ? 'bg-gray-800 text-white border-gray-800'
                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
            }`}
          >
            {s === '' ? 'Tous' : STATUS_LABEL[s]}
            {s !== '' && <span className="ml-1 opacity-70">({codes.filter(c => getCodeStatus(c) === s).length})</span>}
          </button>
        ))}
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Code</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Réduction</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Utilisation</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Validité</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Plans</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Statut</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-6 h-6 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
                    Chargement…
                  </div>
                </td></tr>
              ) : visible.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Aucun code promo{statusFilter ? ' pour ce filtre' : ''}</td></tr>
              ) : visible.map(c => {
                const status = getCodeStatus(c);
                const usePct = c.max_uses ? Math.min(100, Math.round((c.used_count / c.max_uses) * 100)) : 0;
                return (
                  <tr key={c.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-gray-900 text-sm">{c.code}</span>
                        <button
                          onClick={() => copyCode(c.code)}
                          title="Copier le code"
                          className="text-gray-300 hover:text-gray-600 transition"
                        >
                          {copied === c.code
                            ? <FiCheck size={13} className="text-green-500" />
                            : <FiCopy size={13} />
                          }
                        </button>
                      </div>
                      {c.description && <p className="text-xs text-gray-400 truncate max-w-[180px] mt-0.5">{c.description}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-base font-bold ${c.discount_type === 'percentage' ? 'text-pink-600' : 'text-purple-600'}`}>
                        {c.discount_type === 'percentage' ? `${c.discount_value}%` : `${c.discount_value} €`}
                      </span>
                      {c.min_purchase_amount ? (
                        <p className="text-xs text-gray-400">min. {c.min_purchase_amount} €</p>
                      ) : null}
                      {Number(c.total_discount_given) > 0 && (
                        <p className="text-xs text-gray-400">{Number(c.total_discount_given).toFixed(2)} € accordés</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="font-medium">{c.used_count}</span>
                        <span className="text-gray-400">/</span>
                        <span>{c.max_uses ?? '∞'}</span>
                      </div>
                      {c.max_uses !== null && c.max_uses > 0 && (
                        <div className="mt-1.5 w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${usePct >= 100 ? 'bg-red-400' : usePct >= 80 ? 'bg-orange-400' : 'bg-green-400'}`}
                            style={{ width: `${usePct}%` }}
                          />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      <div>Jusqu'au {fmtDate(c.valid_until)}</div>
                    </td>
                    <td className="px-4 py-3">
                      {c.applicable_plans?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {c.applicable_plans.map(p => (
                            <span key={p} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[11px] font-medium">{p}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Tous les plans</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[status]}`}>
                        {STATUS_LABEL[status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(c)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50 transition">
                          <FiEdit2 size={13} />
                        </button>
                        <button onClick={() => setDeleteId(c.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition">
                          <FiTrash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modal formulaire ── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h3 className="font-bold text-gray-900">{editId ? 'Modifier le code' : 'Nouveau code promo'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><FiX size={18} /></button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                  <FiAlertCircle size={14} /> {formError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Code *</label>
                  <input type="text" value={form.code}
                    onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                    disabled={!!editId}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono uppercase disabled:bg-gray-50 focus:ring-2 focus:ring-pink-500"
                    placeholder="WELCOME20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type *</label>
                  <select value={form.discount_type}
                    onChange={e => setForm(f => ({ ...f, discount_type: e.target.value as any }))}
                    disabled={!!editId}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50 focus:ring-2 focus:ring-pink-500">
                    <option value="percentage">Pourcentage (%)</option>
                    <option value="fixed_amount">Montant fixe (€)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Valeur * {form.discount_type === 'percentage' ? '(%)' : '(€)'}
                  </label>
                  <input type="number" value={form.discount_value}
                    onChange={e => setForm(f => ({ ...f, discount_value: e.target.value }))}
                    disabled={!!editId}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50 focus:ring-2 focus:ring-pink-500"
                    placeholder={form.discount_type === 'percentage' ? '20' : '50'} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Achat minimum (€)</label>
                  <input type="number" value={form.min_purchase_amount}
                    onChange={e => setForm(f => ({ ...f, min_purchase_amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500"
                    placeholder="Aucun" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <input type="text" value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500"
                  placeholder="Description optionnelle" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Max utilisations (vide = ∞)</label>
                  <input type="number" value={form.max_uses}
                    onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500"
                    placeholder="Illimité" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Max / client</label>
                  <input type="number" min={1} value={form.max_uses_per_customer}
                    onChange={e => setForm(f => ({ ...f, max_uses_per_customer: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Valide à partir du</label>
                  <input type="date" value={form.valid_from}
                    onChange={e => setForm(f => ({ ...f, valid_from: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Expire le</label>
                  <input type="date" value={form.valid_until}
                    onChange={e => setForm(f => ({ ...f, valid_until: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Plans applicables (vide = tous)</label>
                <div className="flex gap-2 flex-wrap">
                  {PLAN_OPTIONS.map(p => (
                    <button key={p} type="button" onClick={() => togglePlan(p)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                        form.applicable_plans.includes(p)
                          ? 'bg-pink-100 border-pink-400 text-pink-700'
                          : 'bg-gray-50 border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={form.is_active}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                  className="w-4 h-4 accent-pink-600" />
                Actif dès la création
              </label>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
              <button onClick={() => setShowForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition">
                Annuler
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg text-sm font-medium hover:bg-pink-700 transition disabled:opacity-50">
                {saving
                  ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sauvegarde…</>
                  : <><FiCheck size={14} /> {editId ? 'Mettre à jour' : 'Créer le code'}</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal suppression ── */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="font-bold text-gray-900 mb-2">Supprimer ce code ?</h3>
            <p className="text-sm text-gray-600 mb-4">Cette action est irréversible.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteId(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                Annuler
              </button>
              <button onClick={() => handleDelete(deleteId)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPromoCodes;
